import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ordersApi } from "../api/orders";
import { orderCommentsApi } from "../api/orderComments";
import type { Order, OrderAccessType } from "../types";

export function useOrders(filters?: Record<string, string>) {
  return useQuery({
    queryKey: ["orders", filters],
    queryFn: () => ordersApi.list(filters),
  });
}

export function usePenaltyCalc(id: string, enabled = true) {
  return useQuery({
    queryKey: ["orders", id, "penalty-calc"],
    queryFn: () => ordersApi.penaltyCalc(id),
    enabled: !!id && enabled,
  });
}

// Pliki z projektu ZE udostępnione w zamówieniu — pobierane tylko gdy zamówienie ma
// udostępnienie (order.has_project_files_share), dla podwykonawcy i zlecającego.
export function useOrderProjectFiles(id: string, enabled = true) {
  return useQuery({
    queryKey: ["orders", id, "project-files"],
    queryFn: () => ordersApi.projectFiles(id),
    enabled: !!id && enabled,
  });
}

export function useTerminateOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: {
      id: string;
      payload: { amount: string; note?: string; agreement_id?: string; penalty_term_id?: string; delay_days?: number };
    }) => ordersApi.terminate(id, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["orders"] }),
  });
}

export function useAnnulOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      ordersApi.annul(id, { reason }),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ["orders"] });
      qc.invalidateQueries({ queryKey: ["orders", id] });
    },
  });
}

export function useReopenOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      ordersApi.reopen(id, { reason }),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ["orders"] });
      qc.invalidateQueries({ queryKey: ["orders", id] });
    },
  });
}

export function useCloseOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, settlement_note }: { id: string; settlement_note: string }) =>
      ordersApi.close(id, { settlement_note }),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ["orders"] });
      qc.invalidateQueries({ queryKey: ["orders", id] });
    },
  });
}

export function useOverduePenalties() {
  return useQuery({
    queryKey: ["orders", "overdue-penalties"],
    queryFn: () => ordersApi.overduePenalties(),
    staleTime: 0,
  });
}

export function useForgiveDaily() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, note }: { id: string; note: string }) =>
      ordersApi.forgiveDaily(id, note),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["orders", "overdue-penalties"] }),
  });
}

export function useSettlePenalties() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, settlement_note, amount }: { id: string; settlement_note: string; amount?: string }) =>
      ordersApi.settlePenalties(id, settlement_note, amount),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["orders", "overdue-penalties"] }),
  });
}

export function useAppliedPenalties(id: string, enabled = true) {
  return useQuery({
    queryKey: ["orders", id, "applied-penalties"],
    queryFn: () => ordersApi.appliedPenalties(id),
    enabled: !!id && enabled,
    staleTime: 0,
  });
}

export function useOrder(id: string, enabled = true) {
  return useQuery({
    queryKey: ["orders", id],
    queryFn: () => ordersApi.get(id),
    enabled: !!id && enabled,
  });
}

// Powiadomienia o zamówieniu (obserwowanie) — włącz/wyłącz dla zalogowanej osoby.
// Optimistic update: przełącznik ma odpowiadać od razu, a odpowiedź serwera i tak
// przyjdzie zaraz po nim. Przy błędzie wracamy do poprzedniego stanu z cache'a.
export function useOrderSubscription(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) => ordersApi.setSubscription(id, enabled),
    onMutate: async (enabled: boolean) => {
      await qc.cancelQueries({ queryKey: ["orders", id] });
      const previous = qc.getQueryData<Order>(["orders", id]);
      if (previous) {
        qc.setQueryData<Order>(["orders", id], { ...previous, is_subscribed: enabled });
      }
      return { previous };
    },
    onError: (_err, _enabled, context) => {
      if (context?.previous) qc.setQueryData(["orders", id], context.previous);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["orders", id] });
    },
  });
}

// ── Dostęp pracowników GK do zamówienia (sekcja „Dostęp pracowników" na karcie) ──
export function useOrderAccessGrants(orderId: string, enabled = true) {
  return useQuery({
    queryKey: ["orders", orderId, "access-grants"],
    queryFn: () => ordersApi.accessGrants(orderId),
    enabled: !!orderId && enabled,
  });
}

// Po każdej zmianie grantów odświeżamy też samo zamówienie: `granted_staff` (kontakty
// pod zamawiającym) i lista kandydatów pochodzą z tych samych danych.
function useInvalidateOrderAccess(orderId: string) {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ["orders", orderId, "access-grants"] });
    qc.invalidateQueries({ queryKey: ["orders", orderId] });
  };
}

export function useGrantOrderAccess(orderId: string) {
  const invalidate = useInvalidateOrderAccess(orderId);
  return useMutation({
    mutationFn: (payload: { user: string; access_type: OrderAccessType }) =>
      ordersApi.grantAccess(orderId, payload),
    onSuccess: invalidate,
  });
}

export function useUpdateOrderAccessGrant(orderId: string) {
  const invalidate = useInvalidateOrderAccess(orderId);
  return useMutation({
    mutationFn: ({ grantId, ...payload }: { grantId: string; access_type: OrderAccessType }) =>
      ordersApi.updateAccessGrant(orderId, grantId, payload),
    onSuccess: invalidate,
  });
}

export function useRevokeOrderAccessGrant(orderId: string) {
  const invalidate = useInvalidateOrderAccess(orderId);
  return useMutation({
    mutationFn: (grantId: string) => ordersApi.revokeAccessGrant(orderId, grantId),
    onSuccess: invalidate,
  });
}

// „Widok podwykonawcy" — podgląd zamówienia oczami przypisanego podwykonawcy (tylko GK).
export function useContractorOrderPreview(
  id: string,
  enabled = true,
  rates?: "visible" | "hidden",
) {
  return useQuery({
    queryKey: ["orders", id, "contractor-preview", rates ?? "auto"],
    queryFn: () => ordersApi.contractorPreview(id, rates),
    enabled: !!id && enabled,
  });
}

// Ręczne uruchomienie generowania wykazu właścicieli (zamówienia WPW bez pliku). Plik
// powstaje w tle — sekcja sama odpytuje zamówienie, dopóki się nie pojawi.
export function useGenerateOwnersList(orderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => ordersApi.generateOwnersList(orderId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["orders", orderId] }),
  });
}

export function useAddOrderMilestone(orderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { label: string; due_date: string }) =>
      ordersApi.addMilestone(orderId, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["orders", orderId] }),
  });
}

export function useUpdateOrderMilestone(orderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ milestoneId, payload }: {
      milestoneId: string;
      payload: { label?: string; due_date?: string; completed_at?: string | null; sequence?: number };
    }) => ordersApi.updateMilestone(orderId, milestoneId, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["orders", orderId] }),
  });
}

export function useDeleteOrderMilestone(orderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (milestoneId: string) => ordersApi.deleteMilestone(orderId, milestoneId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["orders", orderId] }),
  });
}

export function useMoveOrderMilestone(orderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ milestoneId, direction }: { milestoneId: string; direction: "up" | "down" }) =>
      ordersApi.moveMilestone(orderId, milestoneId, direction),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["orders", orderId] }),
  });
}

// „Oznacz jako ukończony" w „Moich terminach" podwykonawcy — orderId w zmiennych
// mutacji, bo zestawienie miesza kamienie wielu zamówień; po zapisie odświeża
// zestawienie i samo zamówienie (kamień jest tam odhaczony tym samym rekordem).
export function useContractorCompleteMilestone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ orderId, milestoneId }: { orderId: string; milestoneId: string }) =>
      ordersApi.completeMilestoneAsContractor(orderId, milestoneId),
    onSuccess: (_data, { orderId }) => {
      qc.invalidateQueries({ queryKey: ["contractor-todo"] });
      qc.invalidateQueries({ queryKey: ["orders", orderId] });
    },
  });
}

export function useAvailableLineItemTypes(orderId: string, enabled = true) {
  return useQuery({
    queryKey: ["orders", orderId, "available-line-item-types"],
    queryFn: () => ordersApi.availableLineItemTypes(orderId),
    enabled: !!orderId && enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCEOUpdateOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: {
      id: string;
      payload: {
        description?: string;
        deadline?: string | null;
        priority?: string;
        contractor?: string;
        ordered_by?: string | null;
        project?: string;
        line_item_result_files?: { id: string; result_files_expected: boolean }[];
      };
    }) => ordersApi.ceoUpdate(id, payload),
    onSuccess: (_data, { id }) => qc.invalidateQueries({ queryKey: ["orders", id] }),
  });
}

export function useSetLineItemResultFiles() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, entries }: { id: string; entries: { id: string; result_files_expected: boolean }[] }) =>
      ordersApi.setLineItemResultFiles(id, entries),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ["orders", id] });
      // Sekcja plików pozycji pokazuje tylko pozycje z włączonym polem.
      qc.invalidateQueries({ queryKey: ["order-result-files", id] });
    },
  });
}

export function useUploadOrderAttachments() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, files }: { id: string; files: File[] }) =>
      ordersApi.uploadAttachments(id, files),
    onSuccess: (_data, { id }) => qc.invalidateQueries({ queryKey: ["orders", id] }),
  });
}

export function useDeleteOrderAttachment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, fileId }: { id: string; fileId: string }) =>
      ordersApi.deleteAttachment(id, fileId),
    onSuccess: (_data, { id }) => qc.invalidateQueries({ queryKey: ["orders", id] }),
  });
}

export function useAcceptOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => ordersApi.accept(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["orders"] }),
  });
}

// Director/CEO override — force-accept without the contractor.
export function useForceAcceptOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => ordersApi.forceAccept(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["orders"] }),
  });
}

// Technical director (and above) — restore a contractor-rejected order to "Wysłane".
export function useRestoreOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => ordersApi.restore(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["orders"] }),
  });
}

// Zamawiający — przedłużenie terminu przyjęcia zamówienia „Wysłane”.
export function useExtendAcceptanceDeadline() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, confirmation_deadline, reason }: { id: string; confirmation_deadline: string; reason?: string }) =>
      ordersApi.extendAcceptanceDeadline(id, { confirmation_deadline, reason }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["orders"] }),
  });
}

export function useRejectOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, comment }: { id: string; comment: string }) => ordersApi.reject(id, comment),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["orders"] }),
  });
}

export function useDeleteOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => ordersApi.softDelete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["orders"] }),
  });
}

export function useAcknowledgeOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => ordersApi.acknowledge(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["orders"] });
      qc.invalidateQueries({ queryKey: ["orders-pending-count"] });
    },
  });
}

export function usePendingOrdersCount(enabled = true) {
  return useQuery({
    queryKey: ["orders-pending-count"],
    queryFn: () => ordersApi.pendingCount(),
    enabled,
  });
}

export function usePendingChangeRequestsCount(enabled = true) {
  return useQuery({
    queryKey: ["orders-pending-change-requests-count"],
    queryFn: () => ordersApi.pendingChangeRequestsCount(),
    enabled,
  });
}

export function usePendingExternalCostsCount(enabled = true) {
  return useQuery({
    queryKey: ["orders-pending-external-costs-count"],
    queryFn: () => ordersApi.pendingExternalCostsCount(),
    enabled,
  });
}

export function usePendingCommentsCount(enabled = true) {
  return useQuery({
    queryKey: ["orders-pending-comments-count"],
    queryFn: () => orderCommentsApi.pendingCount(),
    enabled,
    staleTime: 30_000,
  });
}
